import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ACTIVITY_TYPE } from '../../constants/searchConstants'
import { searchStateData } from '../../data/searchStateData'
import { InstructorSearchResultProfile } from '../../server/types'
import { instructorSearchConnector } from '../../server/connector/InstructorSearchConnector'
import { userStateData } from '../../data/userStateData'

// Interface for the Search
interface ISearchState {
	selectedActivity: ACTIVITY_TYPE
	quickSearchSelectionActive: boolean // Flag indicating the user has clicked one of the quick search options and is editing it, will grey out unselected
	quickSearchSelectionIndex: number
	quickSearchSelectionHover: boolean
	quickSearchSelectionHoverIndex: number
	quickSearchValuesSet: boolean // Flag if a value has been added to the quick search, used to disable search button
	subFilterSelectionActive: boolean
	subFilterSelectionIndex: number
	subFilterValueCounter: number // flag to force value in button to update on changes
	showQuickSearchMobile: boolean
	showHowItWorks: boolean
	searchRequestInProgress: boolean 
	resultProfiles: InstructorSearchResultProfile[]
	explorePopularProfiles: InstructorSearchResultProfile[]
	exploreMaleProfiles: InstructorSearchResultProfile[]
	exploreFemaleProfiles: InstructorSearchResultProfile[]
	likedProfiles: string[]
	renderProfileLikes: number
	appShowSearchMenu: boolean
	appShowFilterMenu: boolean
}

// Initial state
const initialState: ISearchState = {
	selectedActivity: ACTIVITY_TYPE.SKIING,
	quickSearchSelectionActive: false,
	quickSearchSelectionIndex: 1,
	quickSearchSelectionHover: false,
	quickSearchSelectionHoverIndex: 0,
	quickSearchValuesSet: false,
	subFilterSelectionActive: false,
	subFilterSelectionIndex: 0,
	subFilterValueCounter: 1,
	showQuickSearchMobile: false,
	showHowItWorks: false,
	searchRequestInProgress: false,
	resultProfiles: [],
	explorePopularProfiles: [],
	exploreMaleProfiles: [],
	exploreFemaleProfiles: [],
	likedProfiles: userStateData.likedInstructors,
	renderProfileLikes: 1,
	appShowSearchMenu: false,
	appShowFilterMenu: false
}

// Slice definition
export const searchSlice = createSlice({
	name: 'search',
	initialState,
	reducers: {
		// TODO: deprecated setSearchActivity, remove if still not used when switch is working correctly
		setSearchActivity: (state, action: PayloadAction<ACTIVITY_TYPE>) => {
			state.selectedActivity = action.payload
		},
		updateSearchActivity: (state) => {
			state.selectedActivity = searchStateData.searchActivity
		},
		setQuickSearchSelectionActive: (state, action: PayloadAction<boolean>) => {
			state.quickSearchSelectionActive = action.payload
			state.subFilterSelectionActive = false
		},
		setQuickSearchSelectionIndex: (state, action:PayloadAction<number>) => {
			state.quickSearchSelectionIndex = action.payload
		},
		setQuickSearchSelectionHover: (state, action: PayloadAction<boolean>) => {
			state.quickSearchSelectionHover = action.payload
		},
		setQuickSearchSelectionHoverIndex: (state, action:PayloadAction<number>) => {
			state.quickSearchSelectionHoverIndex = action.payload
		},
		toggleQuickSearchSelectionHover: (state) => {
			state.quickSearchSelectionHover = !state.quickSearchSelectionHover
		},
		updateQuickSearchRequestStatus: (state) => {
			state.searchRequestInProgress = instructorSearchConnector.requestInProgress
		},
		updateQuickSearchValues: (state) => {
			// Called when a value is changes in the QuickSearchOptions components
			state.quickSearchValuesSet = searchStateData.areQuickSearchValuesSet()
		},
		setFilterSelectionActive: (state, action: PayloadAction<boolean>) => {
			state.subFilterSelectionActive = action.payload
			state.quickSearchSelectionActive = false
		},
		setFilterSelectionIndex: (state, action: PayloadAction<number>) => {
			state.subFilterSelectionIndex = action.payload
		},
		updateFilterValueCounter: (state) => {
			state.subFilterValueCounter = Math.random() + 1
		},
		updateResultsProfiles: (state) => {
			state.resultProfiles = searchStateData.searchResultProfiles
		},
		updateExploreProfiles: (state) => {
			state.searchRequestInProgress = instructorSearchConnector.requestInProgress
			state.explorePopularProfiles = searchStateData.explorePopularProfiles
			state.exploreMaleProfiles = searchStateData.exploreMaleProfiles
			state.exploreFemaleProfiles = searchStateData.exploreFemaleProfiles
		},
		searchDataUpdated: (state) => {
			state.searchRequestInProgress = instructorSearchConnector.requestInProgress
			state.quickSearchValuesSet = searchStateData.areQuickSearchValuesSet()
			state.resultProfiles = searchStateData.searchResultProfiles
		},
		setShowQuickSearchMobile: (state, action: PayloadAction<boolean>) => {
			state.showQuickSearchMobile = action.payload
		},
		setShowHowItWorks: (state, action: PayloadAction<boolean>) => {
			state.showHowItWorks = action.payload
		},
		toggleShowHowItWorks: (state) => {
			state.showHowItWorks = state.showHowItWorks === false
		},
		toggleAppShowSearchMenu: (state) => {
			state.appShowSearchMenu = state.appShowSearchMenu === false
		},
		toggleAppShowFilterMenu: (state) => {
			state.appShowFilterMenu = state.appShowFilterMenu === false
		},
		updateLikedProfiles: (state) => {
			// let newArr: string[] = []
			// newArr.concat(userStateData.likedInstructors)
			// state.likedProfiles = [...userStateData.likedInstructors.split(',')]
			// console.log(state.likedProfiles)
			state.likedProfiles = userStateData.likedInstructors
			state.renderProfileLikes = Math.random() + 1
			// console.log(state.likedProfiles)
		}
	}
})

// Export actions
export const { 
	setSearchActivity,
	updateSearchActivity,
	setQuickSearchSelectionActive,
	setQuickSearchSelectionIndex,
	setQuickSearchSelectionHover,
	setQuickSearchSelectionHoverIndex,
	toggleQuickSearchSelectionHover,
	updateQuickSearchRequestStatus,
	updateResultsProfiles,
	updateExploreProfiles,
	updateQuickSearchValues,
	setFilterSelectionActive,
	setFilterSelectionIndex,
	updateFilterValueCounter,
	searchDataUpdated,
	setShowQuickSearchMobile,
	setShowHowItWorks,
	updateLikedProfiles,
	toggleShowHowItWorks,
	toggleAppShowSearchMenu,
	toggleAppShowFilterMenu
} = searchSlice.actions

// Export selectors
// export const selectedActivity = (state: RootState) => state.search.selectedActivity

// Export the reducer
export default searchSlice.reducer
